import { Injectable, signal } from '@angular/core';

export type ToastType = 'success' | 'error';

export interface ValidationDetail {
  field?: string;
  message: string;
}

export interface ToastItem {
  [x: string]: any;
  id: number;
  message: string;
  type: ToastType;
  title?: string;
  details?: (string | ValidationDetail)[];
  code?: string;
}

@Injectable({ providedIn: 'root' })
export class ToastService {
  private nextId = 1;
  private readonly errorToastDurationMs = 5000;
  private readonly successToastDurationMs = 2000;
  protected readonly itemsSignal = signal<ToastItem[]>([]);

  readonly items = this.itemsSignal.asReadonly();

  show(message: string, type: ToastType, durationMs?: number, title = '', details: (string | ValidationDetail)[] = [], code = '', errorObject?: any): void {
    const normalizedMessage = message.trim();
    const normalizedTitle = title.trim();
    if (!normalizedMessage && !normalizedTitle) {
      return;
    }

    // Auto-extract details from error object if not provided
    let finalDetails = details;
    if ((!details || details.length === 0) && errorObject?.details) {
      if (Array.isArray(errorObject.details) && errorObject.details.length > 0) {
        finalDetails = errorObject.details.map((detail: any) => {
          if (typeof detail === 'string') {
            return detail;
          }
          return {
            field: detail?.field || '',
            message: detail?.message || ''
          };
        });
      }
    }

    const toast: ToastItem = {
      id: this.nextId++,
      message: normalizedMessage,
      type,
      title: normalizedTitle || undefined,
      details: finalDetails && finalDetails.length > 0 ? finalDetails : undefined,
      code: code || undefined,
    };

    this.itemsSignal.set([toast]);

    const effectiveDurationMs = durationMs ?? this.getDefaultDuration(type);
    if (effectiveDurationMs > 0) {
      setTimeout(() => this.dismiss(toast.id), effectiveDurationMs);
    }
  }

  showApiError(response: any, defaultMessage = 'An error occurred'): void {
    // Handle direct error object or nested error.error structure
    let errorData = response?.error;

    // If the response itself is an error object with success: false
    if (response?.success === false && response?.error) {
      errorData = response.error;
    }

    // HttpErrorResponse.error is the response body: { success: false, error: { message, details } }
    // Drill into the nested error object when the body follows the API envelope pattern
    if (errorData?.success === false && errorData?.error && typeof errorData.error === 'object') {
      errorData = errorData.error;
    }

    if (!errorData) {
      this.show(defaultMessage, 'error');
      return;
    }

    // Extract message from various possible locations
    const message = errorData.message || errorData.msg || defaultMessage;

    // Extract and normalize details
    let details: (string | ValidationDetail)[] = [];
    if (errorData.details && Array.isArray(errorData.details)) {
      details = errorData.details.map((detail: any) => {
        // If detail is already a ValidationDetail object with field and message
        if (detail && typeof detail === 'object' && detail.message) {
          return {
            field: detail.field || '',
            message: detail.message
          };
        }
        // If detail is a string
        if (typeof detail === 'string') {
          return detail;
        }
        // If detail is some other structure, try to extract message
        if (detail?.message) {
          return detail.message;
        }
        // Fallback
        return String(detail);
      });
    }

    // Extract code/statusCode
    const code = errorData.code || errorData.statusCode || response?.status || '';
    const title = code ? `Error ${code}` : 'Error';

    this.show(message, 'error', undefined, title, details, code);
  }

  showError(message: string, details?: (string | ValidationDetail)[], code?: string, errorObject?: any): void {
    const title = code ? `Error ${code}` : 'Error';
    this.show(message, 'error', undefined, title, details || [], code, errorObject);
  }

  private getDefaultDuration(type: ToastType): number {
    return type === 'error' ? this.errorToastDurationMs : this.successToastDurationMs;
  }

  dismiss(id: number): void {
    this.itemsSignal.update((items) => items.filter((item) => item.id !== id));
  }

  clear(): void {
    this.itemsSignal.set([]);
  }
}
